import createUrlBuilder from 'utilities/url.builder.utils';
import { getRequest, postRequest } from 'utilities/service.utils';
import AppService from './app.service.context';

/**
 * postRequest/getRequest never throw — failures return as resolved values.
 * Same pattern as largeMemberEnquiry.service.ts
 */
function assertNoError(response: any, context: string): void {
  if (!response) {
    throw Object.assign(new Error(`${context}: empty response`), {
      status: 500
    });
  }
  if (response instanceof Error) {
    throw Object.assign(response, { status: 0 });
  }
  if (typeof response !== 'object' || response === null) {
    return;
  }
  const r: {
    error?: { code?: number; message?: string };
    status?: number;
    statusCode?: number;
    errors?: Array<{ message?: string }>;
  } = response;

  if (r.error && typeof r.error === 'object' && r.error.code) {
    const err = r.error;
    const status = typeof err.code === 'number' ? err.code : 400;
    throw Object.assign(
      new Error(`${context}: ${err.message || 'API error'}`),
      { status }
    );
  }
  const status = r.status ?? r.statusCode;
  if (typeof status === 'number' && status >= 400) {
    throw Object.assign(new Error(`${context}: HTTP ${status}`), { status });
  }
  if (r.errors) {
    const errors = r.errors;
    const first = Array.isArray(errors) ? errors[0] : null;
    throw Object.assign(
      new Error(`${context}: ${(first && first.message) || 'API error'}`),
      { status: 400 }
    );
  }
}

/** Logs response + meta when `assertNoError` rejects (Amplify returns errors as values). */
function assertNoErrorWithLog(
  response: any,
  context: string,
  meta: { [key: string]: any } = {}
): void {
  try {
    assertNoError(response, context);
  } catch (error) {
    console.error(`[BEL] ${context}`, { ...meta, response, error }); // eslint-disable-line no-console
    throw error;
  }
}

/** POST /system/uploads — API returns { id, url, method, key } */
export interface PresignedUploadResponse {
  id: number;
  url: string;
  method: string;
  key: string;
}

export interface TransformFileToBulkResponse {
  bulkId: number;
  Job: {
    id: number;
    type: string;
    status: string;
    createdAt?: string;
  };
}

export interface JobStatusResponse {
  id: number;
  type: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  request: { [key: string]: any };
  response: { [key: string]: any };
  createdAt: string;
  updatedAt: string;
}

export interface BulkEnquiryLodgementResponse {
  id: number;
  status: string;
  bufferDistance?: number | null;
  maxSplitAreaSqm?: number | null;
  totalBulkEnquiries?: number | null;
  totalEnquiriesCreated?: number | null;
  originalFileType?: string | null;
  updatedFileId?: number | null;
  UpdatedFile?: { id: number; name: string; mimeType: string } | null;
  BufferJob?: { id: number; type: string; status: string; request?: any };
  SplitJob?: { id: number; type: string; status: string; request?: any };
  ConvertJob?: { id: number; type: string; status: string; request?: any };
  UpdateFilesJob?: {
    id: number;
    type: string;
    status: string;
    request?: any;
  };
  ProcessFilesJob?: {
    id: number;
    type: string;
    status: string;
    request?: any;
  };
}

export interface UpdateBulkLodgementBody {
  bufferDistance?: number;
  maxSplitAreaSqm?: number;
  userReference?: string | null;
  userReferenceField?: string | null;
  digStartAt?: string;
  digEndAt?: string;
  isPlanningJob?: boolean;
  activityTypes?: string[];
  notes?: string | null;
  userTimezone?: string;
  source?: string;
  locationTypes?: ('Road Reserve' | 'Private')[];
  locationsInRoad?: ('Road' | 'Nature Strip' | 'Footpath')[];
  autoRenewUntil?: string | null;
}

export interface UpdateBulkLodgementResponse {
  bulkId: number;
  Jobs: { id: number; type: string; status: string; createdAt?: string }[];
}

export interface ValidateBulkGeometrySettingsBody {
  bufferDistance: number;
  maxSplitAreaSqm?: number | null;
}

export interface ValidateBulkGeometryViolation {
  featureIndex: number;
  geometryType: string;
  bufferedAreaSqm: number;
  code: string;
  message: string;
}

export interface ValidateBulkGeometrySettingsResponse {
  canProceed: boolean;
  requiresSplit: boolean;
  maxBufferedAreaSqm?: number;
  features?: {
    featureIndex: number;
    geometryType: string;
    rawAreaSqm: number | null;
    lineLengthM: number | null;
    bufferedAreaSqm: number;
    exceedsLimit: boolean;
  }[];
  violations: ValidateBulkGeometryViolation[];
}

export const requestPresignedUpload = async (body: {
  name: string;
  mimeType?: string;
}): Promise<PresignedUploadResponse> => {
  const url = createUrlBuilder('/system/uploads').toString();
  const response = await postRequest(AppService, url, body);
  assertNoErrorWithLog(response, 'requestPresignedUpload', {
    url,
    body: { name: body.name }
  });
  return response;
};

export const uploadFileToS3 = async (
  presignedUrl: string,
  file: File
): Promise<Response> => {
  const body = await file.arrayBuffer();
  return await fetch(presignedUrl, {
    method: 'PUT',
    body,
    headers: {
      'Content-Type': file.type || 'application/octet-stream'
    }
  });
};

export const transformFileToBulk = async (
  fileId: number
): Promise<TransformFileToBulkResponse> => {
  const url = createUrlBuilder(`/system/uploads/${fileId}`)
    .addParameter('transform', 'bulk2db')
    .toString();
  const response = await getRequest(AppService, url);
  assertNoErrorWithLog(response, 'transformFileToBulk', { url, fileId });
  return response;
};

export const getJobStatus = async (
  jobId: number
): Promise<JobStatusResponse> => {
  const url = createUrlBuilder(`/system/jobs/${jobId}`).toString();
  const response = await getRequest(AppService, url);
  assertNoErrorWithLog(response, 'getJobStatus', { url, jobId });
  return response;
};

export const getBulkEnquiryLodgement = async (
  bulkId: number,
  include?: string[]
): Promise<BulkEnquiryLodgementResponse> => {
  const builder = createUrlBuilder(`/enquiries/bulk/${bulkId}`);
  if (include?.length) {
    builder.addParameter('include', include.join(','));
  }
  const url = builder.toString();
  const response = await getRequest(AppService, url);
  assertNoErrorWithLog(response, 'getBulkEnquiryLodgement', {
    url,
    bulkId,
    include: include ?? []
  });
  return response;
};

export const updateBulkEnquiryLodgement = async (
  bulkId: number,
  body: UpdateBulkLodgementBody
): Promise<UpdateBulkLodgementResponse> => {
  const url = createUrlBuilder(`/enquiries/bulk/${bulkId}`).toString();
  const response = await postRequest(AppService, url, body);
  try {
    assertNoErrorWithLog(response, 'updateBulkEnquiryLodgement', {
      url,
      bulkId
    });
    return response;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    throw Object.assign(err, { response });
  }
};

export const validateBulkGeometrySettings = async (
  bulkId: number,
  body: ValidateBulkGeometrySettingsBody
): Promise<ValidateBulkGeometrySettingsResponse> => {
  const url = createUrlBuilder(
    `/enquiries/bulk/${bulkId}/validate-geometry-settings`
  ).toString();
  const response = await postRequest(AppService, url, body);
  try {
    assertNoErrorWithLog(response, 'validateBulkGeometrySettings', {
      url,
      bulkId
    });
    return response;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    throw Object.assign(err, { response });
  }
};

/** GET /system/files/{fileId}?format=url */
export const getSystemFileDownloadUrl = async (
  fileId: number
): Promise<{ url: string }> => {
  const url = createUrlBuilder(`/system/files/${fileId}`)
    .addParameter('format', 'url')
    .toString();
  const response = await getRequest(AppService, url);
  assertNoErrorWithLog(response, 'getSystemFileDownloadUrl', { url, fileId });
  return response;
};
